/**
 * PostHog browser cookie helpers and the SSR-only emission entry point.
 *
 * Pure helpers (`getPostHogCookieName`, `serializePostHogCookieValue`,
 * `POSTHOG_COOKIE_MAX_AGE_SECONDS`) are safe in any environment — they're
 * just string/JSON manipulation.
 *
 * `emitPostHogCookieIfNew` is wrapped in `createServerOnlyFn`. The TanStack
 * Start compiler plugin replaces the body with a throw-stub in client
 * builds, so the top-level `setCookie` import (and the
 * `@tanstack/start-server-core` `AsyncLocalStorage` it drags along) is
 * tree-shaken out of the client bundle. The import-protection plugin
 * recognizes `createServerOnlyFn(...)` as a safe boundary for usages of
 * bindings from `@tanstack/react-start/server`. See
 * `docs/cloned-repos-as-docs/router/packages/start-plugin-core/src/import-protection/INTERNALS.md`
 * (Usage Lookup And Safe Boundaries) and `analysis.ts:391` for the rule.
 *
 * **Do NOT name this file `*.server.ts`.** That pattern is in the import-
 * protection plugin's client-env file-deny list (`defaults.ts: files:
 * ['**\/*.server.*']`), and would cause the whole module to be replaced
 * with an empty mock when imported from `__root.tsx` — even though
 * `createServerOnlyFn` would otherwise make the usages safe.
 *
 * Cookie naming follows `@posthog/next`'s `getPostHogCookieName`: the API
 * key is sanitized DIRECTLY (no base64 step) by mapping `+` → `PL`, `/` →
 * `SL`, `=` → `EQ`, then wrapped as `ph_<sanitized>_posthog`. See the
 * dead-end note in §9(b) of `docs/projects/active/a-b-test-public-doc.md`.
 */
import {
  getPostHogCookieName,
  serializePostHogCookieValue as serializeSharedPostHogCookieValue,
} from '@listening/frontend/analytics/posthog-cookie';
import { createServerOnlyFn } from '@tanstack/react-start';
import { setCookie } from '@tanstack/react-start/server';
import { clientEnv } from '@/env';
import type { BootstrapResponse } from '@listening/api-contract';
import {
  randomUUID,
  WebCryptoLive,
} from '@listening/frontend/crypto/web-crypto';
import { Effect } from 'effect';

export { getPostHogCookieName } from '@listening/frontend/analytics/posthog-cookie';

type AnalyticsBootstrapData = typeof BootstrapResponse.Type;

/**
 * Build the JSON payload posthog-js expects in its persistence cookie for
 * an anonymous user. `$device_id` MUST equal `distinct_id` per posthog-js's
 * own anonymous handling.
 */
export function serializePostHogCookieValue(distinctId: string): string {
  // posthog-js's session ID is a UUID. v4 vs v7 doesn't matter for this
  // initial value — posthog-js will manage subsequent session rotation
  // from the client side once it picks up our cookie.
  return serializeSharedPostHogCookieValue(distinctId, {
    anonymousSession: {
      sessionId: Effect.runSync(randomUUID.pipe(Effect.provide(WebCryptoLive))),
      timestampMs: Date.now(),
    },
  });
}

/** One year in seconds; matches lis_bucket and posthog-js's default. */
export const POSTHOG_COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

/**
 * If `Analytics.Bootstrap` minted a fresh `distinctId` (anonymous user with
 * no PostHog cookie on the request), emit a `Set-Cookie` so the browser
 * persists that identity. Without this, every anonymous page view starts
 * a new PostHog session and we never build session continuity.
 *
 * Idempotent / no-op when the bootstrap is `undefined` or
 * `newDistinctIdGenerated` is false.
 */
export const emitPostHogCookieIfNew = createServerOnlyFn(
  (bootstrap: AnalyticsBootstrapData | undefined): void => {
    if (!bootstrap?.newDistinctIdGenerated) return;
    setCookie(
      getPostHogCookieName(clientEnv.posthogPublicKey),
      serializePostHogCookieValue(bootstrap.distinctId),
      {
        path: '/',
        maxAge: POSTHOG_COOKIE_MAX_AGE_SECONDS,
        sameSite: 'lax',
        secure: true,
      },
    );
  },
);
